import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Alert, Box, Flex, toaster } from '@optiaxiom/react';

import { extractApiErrorMessage } from '@frontend/api/http';
import { instanceConfigurationService } from '@frontend/api/services/instanceConfigurationService';
import { opalUserAtom } from '@frontend/atoms';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { InferenceTypeSelector } from '@frontend/components/InferenceTypeSelector';
import { AgentInferenceType } from '@frontend/types/specialized-agent';

function ChatSetupConfiguration() {
  const user = useAtomValue(opalUserAtom);
  const [selectedInference, setSelectedInference] = useState<AgentInferenceType>('simple');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfiguration = async () => {
      if (!user?.instance?.instance_id) return;

      setIsLoading(true);
      setError(null);

      try {
        const config = await instanceConfigurationService.getInstanceConfiguration(
          user.instance.instance_id
        );
        setSelectedInference(config.default_inference_for_primary_agent);
      } catch (err) {
        setError(extractApiErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadConfiguration();
  }, [user?.instance?.instance_id]);

  const handleSet = async (inferenceType: AgentInferenceType) => {
    if (!user?.instance?.instance_id) return;
    if (inferenceType === selectedInference) return;

    setSelectedInference(inferenceType);

    try {
      await instanceConfigurationService.updateInstanceConfiguration(user.instance.instance_id, {
        default_inference_for_primary_agent: inferenceType,
      });
      toaster.create('Configuration updated successfully!', { intent: 'success' });
    } catch (err) {
      const msg = extractApiErrorMessage(err);
      toaster.create(msg, { intent: 'danger' });
    }
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (error) {
    return (
      <Box>
        <Alert intent="danger" title="Error loading configuration">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Flex flexDirection="column" gap="24">
      <Flex flexDirection="column" gap="16">
        <Flex flexDirection="column" gap="8" maxW="md">
          <InferenceTypeSelector
            value={selectedInference}
            onChange={inference_type => handleSet(inference_type)}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

export default ChatSetupConfiguration;
